import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, RefreshCcw, Plus, Pencil, Printer, DoorOpen, AlertTriangle, Download, Filter } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { IncidentModal } from "../components/IncidentModal";
import { api, apiError, priceLabel } from "../lib/api";

const CHART_COLORS = ["#2F557F", "#197FBA", "#16A9D5", "#67BEC7", "#C9C9C9"];
const PRIORITY_LABELS = { low: "Baja", medium: "Media", high: "Alta", critical: "Crítica" };
const PRIORITY_CLS = { low: "bg-hz-gray/30 text-hz-ink", medium: "bg-hz-cyan/10 text-hz-blue", high: "bg-hz-warning/10 text-hz-warning", critical: "bg-hz-error/10 text-hz-error" };
const INC_STATUS_LABELS = { open: "Abierta", in_progress: "En proceso", resolved: "Resuelta", closed: "Cerrada" };
const INC_STATUS_CLS = { open: "bg-hz-error/10 text-hz-error", in_progress: "bg-hz-warning/10 text-hz-warning", resolved: "bg-hz-success/10 text-hz-success", closed: "bg-hz-gray/30 text-hz-ink" };
const WS_STATE_LABELS = { draft: "Borrador", scheduled: "Programado", starting_soon: "Próximo a iniciar", in_progress: "En curso", completed: "Finalizado", closed: "Cerrado", cancelled: "Cancelado" };
const WS_STATE_CLS = { draft: "bg-hz-gray/30 text-hz-ink/70", scheduled: "bg-hz-blue/10 text-hz-blue", starting_soon: "bg-hz-warning/10 text-hz-warning", in_progress: "bg-hz-cyan/10 text-hz-blue", completed: "bg-hz-success/10 text-hz-success", closed: "bg-hz-gray/40 text-hz-ink", cancelled: "bg-hz-ink/10 text-hz-ink/70" };

// ---------------------------------------------------------------------------
// Dashboard ejecutivo
// ---------------------------------------------------------------------------
export const DashboardTab = ({ role }) => {
  const [data, setData] = useState(null);
  const [delegations, setDelegations] = useState([]);
  const [filters, setFilters] = useState({ delegation_id: "", payment_status: "", source: "", from_date: "", to_date: "" });
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/admin/dashboard", { params })
      .then((r) => { setData(r.data); setUpdatedAt(new Date()); })
      .catch((e) => { if (e?.response?.status !== 403) toast.error(apiError(e)); });
  }, [filters]);

  useEffect(() => {
    load();
    api.get("/admin/catalog/delegations").then((r) => setDelegations(r.data.items || [])).catch(() => {});
    const iv = setInterval(load, 60000); // actualización automática cada 60 s
    return () => clearInterval(iv);
  }, [load]);

  if (!data) return <div className="flex items-center gap-2 py-10 text-hz-navy" data-testid="dashboard-loading"><Loader2 className="h-4 w-4 animate-spin" /> Cargando dashboard…</div>;

  const t = data.totals;
  const cards = [
    ["Pre-registrados", t.total_registered, "dash-total"],
    ["Pago aprobado", t.approved, "dash-approved"],
    ["Comprobantes en revisión", t.pending_review, "dash-pending"],
    ["Pagos rechazados", t.rejected, "dash-rejected"],
    ["Cortesías activas", t.courtesies, "dash-courtesies"],
    ["Autorizados a cena", t.dinner_authorized, "dash-dinner-auth"],
    ["Ingresaron a cena", t.dinner_checked, "dash-dinner-check"],
    ["Autorizados evento general", t.general_authorized, "dash-general-auth"],
    ["Ingresaron al evento", t.general_checked, "dash-general-check"],
    ["Asistencia evento general", `${t.general_attendance_pct}%`, "dash-general-pct"],
    ["Registros en sitio", t.onsite, "dash-onsite"],
    ["Check-ins de hoy", t.checkins_today, "dash-today"],
    ["Incidencias abiertas", t.incidents_open, "dash-inc-open"],
    ["Incidencias en proceso", t.incidents_in_progress, "dash-inc-progress"],
    ["Incidencias resueltas", t.incidents_resolved, "dash-inc-resolved"],
  ];

  const Chart = ({ title, children, testId, empty }) => (
    <div className="rounded-xl border border-hz-gray/40 bg-white p-4" data-testid={testId}>
      <p className="text-sm font-bold text-hz-navy">{title}</p>
      {empty ? <p className="py-8 text-center text-xs text-hz-ink/50">Sin datos para los filtros seleccionados.</p> : (
        <div className="mt-2 h-52">{children}</div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="dashboard-tab">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-hz-gray/40 bg-white p-4">
        <Filter className="h-4 w-4 text-hz-ink/40" />
        <div>
          <Label className="text-xs font-semibold">Delegación</Label>
          <select value={filters.delegation_id} onChange={(e) => setFilters({ ...filters, delegation_id: e.target.value })} data-testid="dash-filter-delegation" className="mt-1 h-9 rounded-md border border-input bg-white px-2 text-xs">
            <option value="">Todas</option>
            {delegations.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs font-semibold">Estado de pago</Label>
          <select value={filters.payment_status} onChange={(e) => setFilters({ ...filters, payment_status: e.target.value })} data-testid="dash-filter-payment" className="mt-1 h-9 rounded-md border border-input bg-white px-2 text-xs">
            <option value="">Todos</option>
            <option value="pending">Pendiente</option>
            <option value="receipt_uploaded">En revisión</option>
            <option value="approved">Aprobado</option>
            <option value="rejected">Rechazado</option>
            <option value="waived">Cortesía</option>
          </select>
        </div>
        <div>
          <Label className="text-xs font-semibold">Origen</Label>
          <select value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })} data-testid="dash-filter-source" className="mt-1 h-9 rounded-md border border-input bg-white px-2 text-xs">
            <option value="">Todos</option>
            <option value="online">En línea</option>
            <option value="onsite">En sitio</option>
          </select>
        </div>
        <div>
          <Label className="text-xs font-semibold">Desde</Label>
          <Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} data-testid="dash-filter-from" className="mt-1 h-9 text-xs" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Hasta</Label>
          <Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} data-testid="dash-filter-to" className="mt-1 h-9 text-xs" />
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="dash-refresh-btn" className="font-bold text-hz-navy">
          <RefreshCcw className="mr-1 h-3.5 w-3.5" /> Actualizar
        </Button>
        <p className="text-xs text-hz-ink/50" data-testid="dash-updated">Actualizado: {updatedAt?.toLocaleTimeString("es-MX")}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {cards.map(([label, value, testId]) => (
          <div key={testId} className="rounded-xl border border-hz-gray/40 bg-white p-4" data-testid={testId}>
            <p className="text-2xl font-extrabold text-hz-navy">{value}</p>
            <p className="text-[11px] font-semibold text-hz-ink/60">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Chart title="Registros por día" testId="chart-registrations" empty={!data.series.registrations_by_day.length}>
          <ResponsiveContainer>
            <BarChart data={data.series.registrations_by_day}>
              <XAxis dataKey="day" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} /><Tooltip />
              <Bar dataKey="count" name="Registros" fill="#16A9D5" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Chart>
        <Chart title="Pagos por estado" testId="chart-payments" empty={!data.series.payments_by_status.length}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={data.series.payments_by_status} dataKey="count" nameKey="status" outerRadius={70} label>
                {data.series.payments_by_status.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Chart>
        <Chart title="Check-ins por hora (hoy)" testId="chart-checkins" empty={!data.series.checkins_by_hour.length}>
          <ResponsiveContainer>
            <BarChart data={data.series.checkins_by_hour}>
              <XAxis dataKey="hour" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} /><Tooltip />
              <Bar dataKey="count" name="Check-ins" fill="#2F557F" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Chart>
        <Chart title="Asistentes por delegación" testId="chart-delegations" empty={!data.series.attendees_by_delegation.length}>
          <ResponsiveContainer>
            <BarChart data={data.series.attendees_by_delegation} layout="vertical">
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} /><YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10 }} /><Tooltip />
              <Bar dataKey="count" name="Asistentes" fill="#197FBA" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Chart>
        <Chart title="Inscritos vs presentes por taller" testId="chart-workshops" empty={!data.series.workshops_chart.length}>
          <ResponsiveContainer>
            <BarChart data={data.series.workshops_chart}>
              <XAxis dataKey="name" tick={{ fontSize: 9 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} /><Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="inscritos" name="Inscritos" fill="#67BEC7" radius={[4, 4, 0, 0]} />
              <Bar dataKey="presentes" name="Presentes" fill="#2F557F" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Chart>
        <Chart title="Cena: autorizados vs ingresaron" testId="chart-dinner" empty={!data.series.dinner_chart.length}>
          <ResponsiveContainer>
            <BarChart data={data.series.dinner_chart}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} /><Tooltip />
              <Bar dataKey="count" name="Personas" fill="#16A9D5" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Chart>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-hz-gray/40 bg-white" data-testid="dash-recent-checkins">
          <p className="border-b border-hz-gray/40 px-4 py-3 text-sm font-bold text-hz-navy">Check-ins recientes</p>
          <div className="divide-y divide-hz-gray/30">
            {data.recent_checkins.length === 0 && <p className="px-4 py-5 text-xs text-hz-ink/50">Sin registros.</p>}
            {data.recent_checkins.map((c) => (
              <div key={c.id} className="px-4 py-2.5">
                <p className="text-xs font-bold text-hz-ink">{c.attendee_name} <span className="text-hz-navy">{c.folio}</span></p>
                <p className="text-[11px] text-hz-ink/60">{c.checkpoint_type}{c.workshop_name ? ` · ${c.workshop_name}` : ""} · {new Date(c.created_at).toLocaleString("es-MX")} · {c.usher_name}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-hz-gray/40 bg-white" data-testid="dash-open-incidents">
          <p className="border-b border-hz-gray/40 px-4 py-3 text-sm font-bold text-hz-navy">Incidencias abiertas</p>
          <div className="divide-y divide-hz-gray/30">
            {data.open_incidents.length === 0 && <p className="px-4 py-5 text-xs text-hz-ink/50">Sin incidencias abiertas.</p>}
            {data.open_incidents.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-2 px-4 py-2.5">
                <div>
                  <p className="text-xs font-bold text-hz-ink">{i.category}</p>
                  <p className="text-[11px] text-hz-ink/60">{i.description}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${PRIORITY_CLS[i.priority]}`}>{PRIORITY_LABELS[i.priority]}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-hz-gray/40 bg-white" data-testid="dash-workshops-live">
          <p className="border-b border-hz-gray/40 px-4 py-3 text-sm font-bold text-hz-navy">Talleres ({data.workshops_summary.in_progress} en curso · {data.workshops_summary.starting_soon} próximos)</p>
          <div className="divide-y divide-hz-gray/30">
            {data.workshops.length === 0 && <p className="px-4 py-5 text-xs text-hz-ink/50">Sin talleres.</p>}
            {data.workshops.map((w) => (
              <div key={w.id} className="flex items-center justify-between gap-2 px-4 py-2.5">
                <div>
                  <p className="text-xs font-bold text-hz-ink">{w.name}</p>
                  <p className="text-[11px] text-hz-ink/60">{w.present_count}/{w.registered_count} presentes · {w.available} disponibles</p>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${WS_STATE_CLS[w.state]}`}>{WS_STATE_LABELS[w.state]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Incidencias
// ---------------------------------------------------------------------------
export const IncidentsTab = ({ role }) => {
  const [items, setItems] = useState(null);
  const [filters, setFilters] = useState({ status: "", priority: "" });
  const [selected, setSelected] = useState(null);
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [edit, setEdit] = useState({ status: "", priority: "", assigned_to: "", resolution: "" });

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/incidents", { params }).then((r) => setItems(r.data.incidents || [])).catch((e) => toast.error(apiError(e)));
  }, [filters]);

  useEffect(() => {
    load();
    if (role === "admin" || role === "welcome_coordinator") {
      api.get("/admin/users").then((r) => setUsers(r.data.users || [])).catch(() => {});
    }
  }, [load, role]);

  const save = async () => {
    const payload = {};
    if (edit.status) payload.status = edit.status;
    if (edit.priority && edit.priority !== selected.priority) payload.priority = edit.priority;
    if (edit.assigned_to !== (selected.assigned_to || "")) payload.assigned_to = edit.assigned_to || null;
    if (edit.resolution) payload.resolution = edit.resolution;
    if (!Object.keys(payload).length) { toast.error("Sin cambios."); return; }
    try {
      await api.patch(`/incidents/${selected.id}`, payload);
      toast.success("Incidencia actualizada");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const canManage = role === "admin" || role === "welcome_coordinator";

  return (
    <div data-testid="incidents-tab">
      <div className="flex flex-wrap items-center gap-3">
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="incidents-filter-status" className="h-9 rounded-md border border-input bg-white px-2 text-xs">
          <option value="">Todos los estados</option>
          {Object.entries(INC_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })} data-testid="incidents-filter-priority" className="h-9 rounded-md border border-input bg-white px-2 text-xs">
          <option value="">Todas las prioridades</option>
          {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <Button onClick={() => setCreateOpen(true)} data-testid="incident-create-btn" className="bg-hz-cyan font-bold text-white hover:bg-hz-blue">
          <Plus className="mr-1 h-4 w-4" /> Nueva incidencia
        </Button>
      </div>

      {!items ? (
        <div className="flex items-center gap-2 py-8 text-hz-navy"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      ) : items.length === 0 ? (
        <p className="py-8 text-sm text-hz-ink/60" data-testid="incidents-empty">No hay incidencias con estos filtros.</p>
      ) : (
        <div className="mt-4 divide-y divide-hz-gray/30 rounded-xl border border-hz-gray/40 bg-white">
          {items.map((i) => (
            <button key={i.id} onClick={() => { setSelected(i); setEdit({ status: i.status, priority: i.priority, assigned_to: i.assigned_to || "", resolution: i.resolution || "" }); }}
              data-testid={`incident-row-${i.id}`} className="flex w-full flex-wrap items-center justify-between gap-2 px-4 py-3 text-left hover:bg-hz-bg">
              <div className="min-w-52">
                <p className="text-sm font-bold text-hz-ink">{i.category}</p>
                <p className="text-xs text-hz-ink/60">{i.description.slice(0, 90)}{i.attendee_folio ? ` · Folio: ${i.attendee_folio}` : ""}{i.workshop_name ? ` · ${i.workshop_name}` : ""}</p>
                <p className="text-[11px] text-hz-ink/50">{i.reported_by_name} · {new Date(i.created_at).toLocaleString("es-MX")}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${PRIORITY_CLS[i.priority]}`}>{PRIORITY_LABELS[i.priority]}</span>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${INC_STATUS_CLS[i.status]}`}>{INC_STATUS_LABELS[i.status]}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <IncidentModal open={createOpen} onClose={(created) => { setCreateOpen(false); if (created) load(); }} />

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-xl" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-bold text-hz-navy" data-testid="incident-detail-title">{selected?.category}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <p className="text-sm text-hz-ink">{selected.description}</p>
              <p className="text-xs text-hz-ink/60">
                Reportó: {selected.reported_by_name} · {new Date(selected.created_at).toLocaleString("es-MX")}
                {selected.attendee_folio ? ` · Folio: ${selected.attendee_folio} (${selected.attendee_name})` : ""}
                {selected.workshop_name ? ` · Taller: ${selected.workshop_name}` : ""}
              </p>
              {canManage ? (
                <div className="space-y-3 rounded-xl border border-hz-gray/40 bg-hz-bg p-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold">Estado</Label>
                      <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                        <SelectTrigger data-testid="incident-edit-status" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(INC_STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold">Prioridad</Label>
                      <Select value={edit.priority} onValueChange={(v) => setEdit({ ...edit, priority: v })}>
                        <SelectTrigger data-testid="incident-edit-priority" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(PRIORITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Responsable asignado</Label>
                    <Select value={edit.assigned_to || "none"} onValueChange={(v) => setEdit({ ...edit, assigned_to: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="incident-edit-assignee" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin asignar</SelectItem>
                        {users.filter((u) => u.active).map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Comentario de resolución (obligatorio al resolver)</Label>
                    <Textarea value={edit.resolution} onChange={(e) => setEdit({ ...edit, resolution: e.target.value })} data-testid="incident-edit-resolution" className="mt-1 bg-white" />
                  </div>
                  <Button onClick={save} data-testid="incident-save-btn" className="w-full bg-hz-navy font-bold text-white">Guardar cambios</Button>
                </div>
              ) : null}
              {selected.history?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-hz-blue">Bitácora</p>
                  <div className="mt-2 space-y-2">
                    {selected.history.map((h, i) => (
                      <p key={i} className="rounded-lg bg-hz-bg px-3 py-2 text-xs text-hz-ink/70">
                        <span className="font-semibold">{new Date(h.at).toLocaleString("es-MX")}</span> — {h.note}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Auditoría
// ---------------------------------------------------------------------------
export const AuditTab = () => {
  const [logs, setLogs] = useState(null);
  const [filters, setFilters] = useState({ action: "", entity_type: "", from_date: "", to_date: "" });

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/admin/audit", { params }).then((r) => setLogs(r.data.logs || [])).catch((e) => toast.error(apiError(e)));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="audit-tab">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-hz-gray/40 bg-white p-4">
        <div>
          <Label className="text-xs font-semibold">Acción</Label>
          <Input value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} data-testid="audit-filter-action" className="mt-1 h-9 text-xs" placeholder="Ej. payment, timer, checkin" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Módulo / entidad</Label>
          <Input value={filters.entity_type} onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })} data-testid="audit-filter-entity" className="mt-1 h-9 text-xs" placeholder="Ej. payment, workshop" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Desde</Label>
          <Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} data-testid="audit-filter-from" className="mt-1 h-9 text-xs" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Hasta</Label>
          <Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} data-testid="audit-filter-to" className="mt-1 h-9 text-xs" />
        </div>
      </div>
      {!logs ? (
        <div className="flex items-center gap-2 py-8 text-hz-navy"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      ) : logs.length === 0 ? (
        <p className="py-8 text-sm text-hz-ink/60" data-testid="audit-empty">Sin registros de bitácora con estos filtros.</p>
      ) : (
        <div className="mt-4 divide-y divide-hz-gray/30 rounded-xl border border-hz-gray/40 bg-white">
          {logs.map((l) => (
            <div key={l.id} className="px-4 py-3" data-testid={`audit-row-${l.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-bold text-hz-navy">{l.action}</p>
                <p className="text-[11px] text-hz-ink/50">{new Date(l.created_at).toLocaleString("es-MX")} · {l.user_name}</p>
              </div>
              <p className="text-xs text-hz-ink/60">{l.entity_type} · {l.entity_id}</p>
              {(l.previous_data || l.new_data) && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-[11px] font-semibold text-hz-cyan">Ver cambios</summary>
                  <div className="mt-1 grid gap-2 sm:grid-cols-2">
                    <pre className="overflow-x-auto rounded-lg bg-hz-bg p-2 text-[10px] text-hz-ink/70">Antes: {JSON.stringify(l.previous_data, null, 1)}</pre>
                    <pre className="overflow-x-auto rounded-lg bg-hz-bg p-2 text-[10px] text-hz-ink/70">Después: {JSON.stringify(l.new_data, null, 1)}</pre>
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Reportes
// ---------------------------------------------------------------------------
const REPORTS = [
  { id: "asistentes", label: "Registro general de asistentes" },
  { id: "pagos", label: "Pagos", adminOnly: true },
  { id: "cena", label: "Cena especial" },
  { id: "evento-general", label: "Evento general" },
  { id: "talleres", label: "Por taller" },
  { id: "organizacion", label: "Por delegación, sector y célula" },
  { id: "equipo", label: "Actividad del equipo de bienvenida" },
  { id: "incidencias", label: "Incidencias" },
  { id: "financiero", label: "Financiero operativo", adminOnly: true },
];

export const ReportsTab = ({ role }) => {
  const available = REPORTS.filter((r) => !r.adminOnly || role === "admin");
  const [report, setReport] = useState(available[0]?.id || "asistentes");
  const [filters, setFilters] = useState({ from_date: "", to_date: "", status: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const canExport = role === "admin" || role === "welcome_coordinator";

  const load = useCallback(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get(`/admin/reports/${report}`, { params })
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); toast.error(apiError(e)); })
      .finally(() => setLoading(false));
  }, [report, filters]);

  useEffect(() => { load(); }, [load]);

  const download = async (format) => {
    setDownloading(format);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data: blob, headers } = await api.get(`/admin/reports/${report}`, {
        params: { ...params, format }, responseType: "blob",
      });
      const dispo = headers["content-disposition"] || "";
      const match = dispo.match(/filename="(.+)"/);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : `reporte_${report}_Horizonte2026.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Reporte ${format.toUpperCase()} descargado`);
    } catch (err) {
      toast.error(err?.response?.status === 403 ? "Tu rol no permite exportar este reporte." : "No se pudo exportar el reporte.");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="reports-tab">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-hz-gray/40 bg-white p-4">
        <div>
          <Label className="text-xs font-semibold">Reporte</Label>
          <Select value={report} onValueChange={setReport}>
            <SelectTrigger data-testid="report-select" className="mt-1 w-64"><SelectValue /></SelectTrigger>
            <SelectContent>
              {available.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`report-option-${r.id}`}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold">Desde</Label>
          <Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} data-testid="report-filter-from" className="mt-1 h-9 text-xs" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Hasta</Label>
          <Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} data-testid="report-filter-to" className="mt-1 h-9 text-xs" />
        </div>
        {report === "incidencias" && (
          <div>
            <Label className="text-xs font-semibold">Estado</Label>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="report-filter-status" className="mt-1 h-9 rounded-md border border-input bg-white px-2 text-xs">
              <option value="">Todos</option>
              {Object.entries(INC_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        )}
        {canExport && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => download("csv")} disabled={!!downloading} data-testid="report-csv-btn" className="font-bold text-hz-navy">
              {downloading === "csv" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />} CSV
            </Button>
            <Button size="sm" variant="outline" onClick={() => download("xlsx")} disabled={!!downloading} data-testid="report-xlsx-btn" className="font-bold text-hz-navy">
              {downloading === "xlsx" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />} XLSX
            </Button>
          </div>
        )}
      </div>

      {report === "financiero" && (
        <p className="rounded-xl border border-hz-warning/30 bg-hz-warning/5 px-4 py-3 text-xs font-medium text-hz-ink" data-testid="financial-disclaimer">
          Este reporte es operativo y se basa en los registros del sistema. No sustituye una conciliación bancaria o contable definitiva.
        </p>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-hz-navy"><Loader2 className="h-4 w-4 animate-spin" /> Generando vista previa…</div>
      ) : !data ? null : (
        <>
          <div className="flex flex-wrap gap-3 rounded-xl border border-hz-gray/40 bg-hz-bg px-4 py-3" data-testid="report-summary">
            {data.summary.map(([k, v]) => (
              <p key={k} className="text-xs"><span className="font-semibold text-hz-ink/60">{k}:</span> <span className="font-extrabold text-hz-navy">{v}</span></p>
            ))}
          </div>
          <div className="overflow-x-auto rounded-xl border border-hz-gray/40 bg-white" data-testid="report-preview">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {data.columns.map((c) => <TableHead key={c} className="whitespace-nowrap font-bold text-hz-navy">{c}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.length === 0 && (
                  <TableRow><TableCell colSpan={data.columns.length} className="py-6 text-center text-sm text-hz-ink/50">Sin registros para los filtros seleccionados.</TableCell></TableRow>
                )}
                {data.rows.slice(0, 100).map((r, i) => (
                  <TableRow key={i}>{r.map((cell, j) => <TableCell key={j} className="text-xs">{cell}</TableCell>)}</TableRow>
                ))}
              </TableBody>
            </Table>
            {data.rows.length > 100 && <p className="px-4 py-2 text-[11px] text-hz-ink/50">Vista previa limitada a 100 filas; la exportación incluye todo.</p>}
          </div>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Talleres Pro (gestión avanzada)
// ---------------------------------------------------------------------------
const EMPTY_WS = {
  name: "", description: "", speaker_name: "", responsible_user_id: "", room_name: "", room_location: "",
  access_open_at: "", starts_at: "", ends_at: "", total_duration_minutes: "", presentation_minutes: "",
  qa_minutes: "", capacity: 80, price: 0, pending_configuration: true, status: "draft",
  instructions: "", sort_order: 0, label_color: "", image_url: "", auto_qa_transition: true,
};

const WsField = ({ label, k, type = "text", testId, value, onChange }) => (
  <div>
    <Label className="text-xs font-semibold">{label}</Label>
    <Input type={type} value={value ?? ""} onChange={(e) => onChange(k, e.target.value)} data-testid={testId || `ws-form-${k}`} className="mt-1" />
  </div>
);

export const WorkshopsTabPro = ({ role }) => {
  const navigate = useNavigate();
  const [workshops, setWorkshops] = useState(null);
  const [roomManagers, setRoomManagers] = useState([]);
  const [filters, setFilters] = useState({ status: "", room: "", responsible: "" });
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY_WS);
  const [conflicts, setConflicts] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/workshops").then((r) => setWorkshops(r.data.workshops || [])).catch((e) => toast.error(apiError(e)));
    api.get("/admin/users").then((r) => setRoomManagers((r.data.users || []).filter((u) => u.role === "room_manager" && u.active))).catch(() => {});
    api.get("/admin/workshop-conflicts").then((r) => setConflicts(r.data.conflicts || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (w) => {
    setForm({
      ...EMPTY_WS, ...Object.fromEntries(Object.keys(EMPTY_WS).map((k) => [k, w[k] ?? EMPTY_WS[k]])),
      responsible_user_id: w.responsible_user_id || "",
    });
    setDialog(w);
  };

  const save = async () => {
    const payload = { ...form };
    for (const k of ["total_duration_minutes", "presentation_minutes", "qa_minutes", "sort_order"]) {
      payload[k] = payload[k] === "" ? null : Number(payload[k]);
    }
    payload.capacity = Number(payload.capacity) || 0;
    payload.price = Number(payload.price) || 0;
    payload.responsible_user_id = payload.responsible_user_id || null;
    for (const k of ["speaker_name", "room_name", "room_location", "access_open_at", "starts_at", "ends_at", "instructions", "label_color", "image_url", "description"]) {
      payload[k] = payload[k] || (k === "description" ? "" : null);
    }
    try {
      const res = dialog?.id
        ? await api.patch(`/admin/workshops/${dialog.id}`, payload)
        : await api.post("/admin/workshops", payload);
      toast.success(dialog?.id ? "Taller actualizado" : "Taller creado");
      (res.data.warnings || []).forEach((w) => toast.warning(w, { duration: 6000 }));
      setDialog(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const filtered = (workshops || []).filter((w) =>
    (!filters.status || w.status === filters.status) &&
    (!filters.room || (w.room_name || "").toLowerCase().includes(filters.room.toLowerCase())) &&
    (!filters.responsible || w.responsible_user_id === filters.responsible)
  );

  const setWsField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="workshops-pro-tab">
      <div className="flex flex-wrap items-center gap-3">
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="ws-filter-status" className="h-9 rounded-md border border-input bg-white px-2 text-xs">
          <option value="">Todos los estados</option>
          {["draft", "published", "closed", "cancelled", "completed"].map((s) => <option key={s} value={s}>{WS_STATE_LABELS[s] || s}</option>)}
        </select>
        <Input value={filters.room} onChange={(e) => setFilters({ ...filters, room: e.target.value })} placeholder="Filtrar por salón" data-testid="ws-filter-room" className="h-9 w-40 text-xs" />
        <select value={filters.responsible} onChange={(e) => setFilters({ ...filters, responsible: e.target.value })} data-testid="ws-filter-responsible" className="h-9 rounded-md border border-input bg-white px-2 text-xs">
          <option value="">Todos los responsables</option>
          {roomManagers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <Button onClick={() => { setForm(EMPTY_WS); setDialog({}); }} data-testid="ws-add-btn" className="bg-hz-cyan font-bold text-white hover:bg-hz-blue">
          <Plus className="mr-1 h-4 w-4" /> Nuevo taller
        </Button>
      </div>

      {conflicts.length > 0 && (
        <div className="mt-3 rounded-xl border border-hz-warning/50 bg-hz-warning/5 p-4" data-testid="ws-conflicts-section">
          <p className="flex items-center gap-2 text-sm font-bold text-hz-warning">
            <AlertTriangle className="h-4 w-4" /> Conflictos de talleres por revisar ({conflicts.length})
          </p>
          <p className="mt-1 text-xs text-hz-ink/70">Estos asistentes tienen talleres vigentes con horarios traslapados. No se canceló nada automáticamente; revisa y ajusta con el asistente.</p>
          <div className="mt-2 space-y-1">
            {conflicts.map((c, i) => (
              <p key={i} className="text-xs text-hz-ink" data-testid={`ws-conflict-row-${c.attendee_id}`}>
                <span className="font-bold">{c.full_name} ({c.folio})</span>: "{c.a_name}" ({c.a_schedule || "horario por confirmar"}) coincide con "{c.b_name}" ({c.b_schedule || "horario por confirmar"})
              </p>
            ))}
          </div>
        </div>
      )}

      {!workshops ? (
        <div className="flex items-center gap-2 py-8 text-hz-navy"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      ) : filtered.length === 0 ? (
        <p className="py-8 text-sm text-hz-ink/60" data-testid="ws-empty">Sin talleres con estos filtros.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {filtered.map((w) => (
            <div key={w.id} className="rounded-xl border border-hz-gray/40 bg-white p-4" data-testid={`ws-row-${w.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  {w.label_color && <span className="mt-1.5 h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: w.label_color }} />}
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-bold text-hz-ink">{w.name}</p>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${WS_STATE_CLS[w.state]}`} data-testid={`ws-state-${w.id}`}>{WS_STATE_LABELS[w.state]}</span>
                      {w.occupancy === "full" && <span className="rounded-full bg-hz-error/10 px-2.5 py-0.5 text-xs font-bold text-hz-error">Lleno</span>}
                      {w.occupancy === "almost_full" && <span className="rounded-full bg-hz-warning/10 px-2.5 py-0.5 text-xs font-bold text-hz-warning">Casi lleno</span>}
                    </div>
                    <p className="mt-1 text-xs text-hz-ink/60">
                      {w.speaker_name || "Ponente —"} · Responsable: {w.responsible_name || "—"} · {w.room_name || "Salón —"}{w.room_location ? ` (${w.room_location})` : ""}
                    </p>
                    <p className="text-xs text-hz-ink/60">
                      {w.starts_at ? new Date(w.starts_at).toLocaleString("es-MX") : "Horario por confirmar"}
                      {" · "}Cupo {w.capacity} · Inscritos {w.registered_count} · Presentes {w.present_count} · Disponibles {w.available} · {priceLabel(w.price, w.pending_configuration)}
                    </p>
                    {(w.warnings || []).length > 0 && (
                      <div className="mt-2 space-y-1" data-testid={`ws-warnings-${w.id}`}>
                        {w.warnings.map((warn, i) => (
                          <p key={i} className="flex items-center gap-1.5 text-xs font-medium text-hz-warning"><AlertTriangle className="h-3.5 w-3.5" /> {warn}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(w)} data-testid={`ws-edit-${w.id}`} className="font-bold text-hz-navy"><Pencil className="mr-1 h-3.5 w-3.5" /> Editar</Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/salon/${w.id}`)} data-testid={`ws-ops-${w.id}`} className="font-bold text-hz-blue"><DoorOpen className="mr-1 h-3.5 w-3.5" /> Vista operativa</Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/admin/print/taller/${w.id}`)} data-testid={`ws-print-${w.id}`} className="font-bold text-hz-navy"><Printer className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-bold text-hz-navy">{dialog?.id ? "Editar taller" : "Nuevo taller"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <WsField label="Nombre *" k="name" testId="ws-form-name" value={form["name"]} onChange={setWsField} />
            <WsField label="Ponente" k="speaker_name" value={form["speaker_name"]} onChange={setWsField} />
            <div className="sm:col-span-2">
              <WsField label="Descripción" k="description" value={form["description"]} onChange={setWsField} />
            </div>
            <div>
              <Label className="text-xs font-semibold">Responsable de salón</Label>
              <Select value={form.responsible_user_id || "none"} onValueChange={(v) => setForm({ ...form, responsible_user_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="ws-form-responsible" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {roomManagers.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`ws-responsible-${u.id}`}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <WsField label="Salón" k="room_name" value={form["room_name"]} onChange={setWsField} />
            <WsField label="Ubicación del salón" k="room_location" value={form["room_location"]} onChange={setWsField} />
            <WsField label="Apertura de acceso" k="access_open_at" type="datetime-local" value={form["access_open_at"]} onChange={setWsField} />
            <WsField label="Inicio oficial" k="starts_at" type="datetime-local" value={form["starts_at"]} onChange={setWsField} />
            <WsField label="Finalización oficial" k="ends_at" type="datetime-local" value={form["ends_at"]} onChange={setWsField} />
            <WsField label="Duración total (min)" k="total_duration_minutes" type="number" value={form["total_duration_minutes"]} onChange={setWsField} />
            <WsField label="Minutos de exposición" k="presentation_minutes" type="number" value={form["presentation_minutes"]} onChange={setWsField} />
            <WsField label="Minutos de Q&A" k="qa_minutes" type="number" value={form["qa_minutes"]} onChange={setWsField} />
            <WsField label="Cupo máximo" k="capacity" type="number" value={form["capacity"]} onChange={setWsField} />
            <WsField label="Costo (MXN)" k="price" type="number" value={form["price"]} onChange={setWsField} />
            <WsField label="Orden de visualización" k="sort_order" type="number" value={form["sort_order"]} onChange={setWsField} />
            <WsField label="Color de etiqueta (hex)" k="label_color" value={form["label_color"]} onChange={setWsField} />
            <WsField label="URL de imagen (opcional)" k="image_url" value={form["image_url"]} onChange={setWsField} />
            <div>
              <Label className="text-xs font-semibold">Estado</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="ws-form-status" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["draft", "published", "closed", "cancelled", "completed"].map((s) => <SelectItem key={s} value={s}>{WS_STATE_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold">Instrucciones para responsable de salón</Label>
              <Textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} data-testid="ws-form-instructions" className="mt-1" />
            </div>
            <label className="flex items-center gap-2 text-sm font-medium text-hz-ink">
              <Switch checked={form.auto_qa_transition} onCheckedChange={(v) => setForm({ ...form, auto_qa_transition: v })} data-testid="ws-form-auto-qa" />
              Cambio automático de exposición a Q&A
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-hz-ink">
              <Switch checked={form.pending_configuration} onCheckedChange={(v) => setForm({ ...form, pending_configuration: v })} data-testid="ws-form-pending" />
              Precio pendiente de configuración
            </label>
          </div>
          <Button onClick={save} data-testid="ws-form-save" className="mt-4 w-full bg-hz-navy font-bold text-white">Guardar taller</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
